import React from 'react';

// Types
import { GoalItem } from 'Shared/constants/types';
import { ViewType } from '../GoalProgress/GoalProgress';

// Components
import GoalProgressWrapper from '../GoalProgressWrapper/GoalProgressWrapper';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';

type Props = {
  goal: GoalItem;
  showLabel?: boolean;
  showEmptyState?: boolean;
  view?: ViewType;
};

const GoalActionItemsProgress = ({ goal, showEmptyState = false, view = 'table' }: Props) => {
  const { actionItems } = useGoal({ slug: goal.slug });

  // These are necessary because in the goals detail page
  // You can complete action items from within the page so the calculation of
  // action item progress must be based on the action items in the redux store
  // instead of the goal serializer
  const actionItemsLengthFromReduxStore = actionItems.length;
  const completedActionItemsLengthFromReduxStore = actionItems.filter(({ completed }) => completed).length;

  const calculateProgressFromReduxStore = view === 'page' || view === 'panel';
  const numCompleteActionItems = calculateProgressFromReduxStore
    ? completedActionItemsLengthFromReduxStore
    : goal.numCompleteActionItems;
  const numIncompleteActionItems = calculateProgressFromReduxStore
    ? actionItemsLengthFromReduxStore - completedActionItemsLengthFromReduxStore
    : goal.numIncompleteActionItems;

  const totalActionItems = numCompleteActionItems + numIncompleteActionItems;

  if (totalActionItems === 0 && showEmptyState)
    return (
      <EmptyStateContainer
        subTitle="Create action items to start tracking progress"
        grayBackground
        padding="025"
        inContainer
      />
    );

  if (totalActionItems === 0) return null;

  const percent = Math.round((numCompleteActionItems * 100) / totalActionItems);

  if (view === 'table') return <GoalProgressWrapper percent={percent} />;
  if (view === 'panel')
    return (
      <GoalProgressWrapper
        percent={percent}
        bottomLabel={{ label: `${numCompleteActionItems} of ${totalActionItems} action items complete` }}
        topLabel={{
          label: `${percent}%`,
          size: 'md',
        }}
      />
    );
  if (view === 'page')
    return (
      <GoalProgressWrapper
        percent={percent}
        bottomLabel={{ label: `${numCompleteActionItems} of ${totalActionItems} complete` }}
        topLabel={{
          label: `${percent}%`,
          size: 'lg',
        }}
      />
    );
};

export default GoalActionItemsProgress;
